/**
 * Register B3 — "Stage timeline in the property dossier. Stages across the top
 * with dwell days beneath each."
 *
 * Reads GET /loans/{id}/stage-timeline, which derives from the same append-only
 * stage lineage as the grid's Days in Status column (register E5). One source,
 * so the strip and the column cannot drift apart.
 *
 * The strip shows the stages this deal has actually occupied, in order, not the
 * full stage vocabulary: a deal that skipped Rate Lock never sat there, and
 * padding the strip with never-visited stages would imply a dwell of zero days
 * where the truth is "did not apply".
 */

import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { Loader2 } from 'lucide-react'

import { loansService, type StageDwellSegment } from '@/services/loans'
import { httpStatus, timelineLoadMessage } from '@/components/deal/dossier/httpStatus'

function dwellLabel(segment: StageDwellSegment): string {
  // Same-day moves are real and common at the top of the funnel — "0d" is the
  // honest reading, not a rounding artefact to hide.
  return `${segment.days}d`
}

/** What the strip's figures mean, said once. */
const DWELL_NOTE =
  'Days shown are dwell in each stage, never cumulative. The highlighted stage is where the deal sits now. Select a stage to open its record.'

export function StageTimeline({
  loanId,
  onSelectSegment,
  compact = false,
}: {
  loanId: string
  /** Register F3 — selecting a stage opens its record. */
  onSelectSegment?: (index: number) => void
  /**
   * A strip over the stage record on a one-screen view (Underwriting >
   * Workflow): tighter boxes, the dwell days still beneath each stage (B3),
   * and the note folded into the heading line instead of a line of its own —
   * at 1366x768 every line here is a line the stage record below cannot show.
   */
  compact?: boolean
}) {
  const timeline = useQuery({
    queryKey: ['loan', loanId, 'stage-timeline'],
    queryFn: () => loansService.getStageTimeline(loanId),
    enabled: Boolean(loanId),
    // A 404 is not a failure here: demo-environment deals are fixtures with no
    // server-side row, so the timeline legitimately has nothing to read.
    // Retrying it just delays the empty state behind three failed requests.
    retry: (count, err) => {
      const status = httpStatus(err)
      return status !== 401 && status !== 403 && status !== 404 && count < 2
    },
  })

  const missing = httpStatus(timeline.error) === 404

  if (timeline.isLoading) {
    return (
      <div className="flex items-center gap-2 px-1 py-3 text-[11px] text-stone-500">
        <Loader2 size={12} className="animate-spin" /> Loading the stage timeline…
      </div>
    )
  }

  if (timeline.error && !missing) {
    return (
      <div className="px-1 py-3 text-[11px] text-red-300">
        {timelineLoadMessage(timeline.error)}
      </div>
    )
  }

  const segments = timeline.data?.segments ?? []
  if (segments.length === 0) {
    return (
      <div className="px-1 py-3 text-[11px] text-stone-500">
        No stage history recorded for this deal yet.
      </div>
    )
  }

  return (
    <div className={compact ? 'px-1 py-1.5' : 'px-1 py-2'}>
      <div className={`flex items-baseline justify-between gap-3 ${compact ? 'mb-1' : 'mb-2'}`}>
        <span className="flex min-w-0 items-baseline gap-2">
          <span className="shrink-0 font-mono text-[9px] uppercase tracking-widest text-stone-500">
            Stage timeline
          </span>
          {compact ? (
            <span className="truncate text-[9px] text-stone-600" title={DWELL_NOTE}>
              Dwell in each stage, never cumulative · select a stage to open its record
            </span>
          ) : null}
        </span>
        <span className="shrink-0 text-[10px] text-stone-500">
          {timeline.data?.totalDaysOpen ?? 0}d total
        </span>
      </div>

      <ol className="flex items-stretch gap-1 overflow-x-auto pb-1">
        {segments.map((segment, i) => (
          <li
            key={`${segment.stage}-${segment.enteredAt}-${i}`}
            className="flex min-w-[104px] flex-1 flex-col"
          >
            <button
              type="button"
              onClick={() => onSelectSegment?.(i)}
              title={`Open the ${segment.stage} record`}
              className="flex flex-col text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400/60"
            >
            <div
              className={clsx(
                'truncate rounded-t border-x border-t px-2 text-[10px] font-medium',
                compact ? 'py-0.5' : 'py-1',
                segment.current
                  ? 'border-amber-400/40 bg-amber-400/10 text-amber-200'
                  : 'border-stone-700/60 bg-stone-800/40 text-stone-300',
              )}
              title={segment.stage}
            >
              {segment.stage}
            </div>
            <div
              className={clsx(
                'rounded-b border px-2 text-center font-mono text-[11px]',
                compact ? 'py-0.5' : 'py-1',
                segment.current
                  ? 'border-amber-400/40 bg-black/40 text-amber-200'
                  : 'border-stone-700/60 bg-black/30 text-stone-400',
              )}
            >
              {dwellLabel(segment)}
            </div>
            </button>
          </li>
        ))}
      </ol>

      {compact ? null : <p className="mt-1.5 text-[9px] text-stone-600">{DWELL_NOTE}</p>}
    </div>
  )
}

export default StageTimeline
